import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import L from 'leaflet';
import FormattedField from './CaseUtils';
import InfoGrid from '../../Shared/InfoGrid';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MultiMarkerMap from './MultiMarkerMap';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Images
import placeOfCenterIcon from '../../../assets/img/hospital-location-pin.svg';

// Styles
import DetailedCaseInfoStyles from '../../../assets/jss/Components/Cases/DetailedCaseInfoStyles';

const InjuryMarker = new L.Icon({
    iconUrl: placeOfCenterIcon,
    iconRetinaUrl: placeOfCenterIcon,
    iconAnchor: null,
    popupAnchor: [0, -10],
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null,
    iconSize: new L.Point(40, 40),
    className: 'leaflet-marker-icon leaflet-zoom-animated leaflet-interactive',
});

export const infoToShow = [
    'diagnosis',
    'routeToDiagnosis',
    'diagnosticTests',
    'postTreatmentSurveillance',
    'cancerType',
    'cancerSubType',
    'clinicalStage',
    'responseToTreatment',
    'tumorAreas',
    'geneMutations',
    'icd10Code',
    'otherConditions',
    'treatmentProcedure',
    'drugTreatment',
    'isTrial',
    'trialId',
];

export const BasicInfo = ({ info, ...props }) => {
    const { classes } = DetailedCaseInfoStyles();

    const getCoordinates = (departmentName) => {
        switch (departmentName) {
            case 'General Oncology Department':
                return { latitude: 29.70723, longitude: -95.397186 };
            case 'Gastrointestinal Oncology Department':
                return { latitude: 44.0223132, longitude: -92.4667642 };
            case 'Breast Oncology Department':
                return { latitude: 40.7641906, longitude: -73.9559261 };
            case 'Gynaecologic Oncology Department':
                return { latitude: 42.3363964, longitude: -71.1072003 };
            case 'Genitourinary Oncology Department':
                return { latitude: 42.337417, longitude: -71.1083302 };
            case 'Neuro-Oncology Department':
                return { latitude: 33.4820158, longitude: -112.0786127 };
            case 'Pediatric Oncology Departament':
                return { latitude: 33.4820158, longitude: -112.0786127 };
            default:
                return null; // or { latitude: 0, longitude: 0 } if you want a fallback location
        }
    };
    const combineLocations = () => {
        const result = [];

        if (info.treatmentLocation && info.treatmentLocation) {
            result.push({
                latitude: getCoordinates(info.treatmentLocation).latitude,
                longitude: getCoordinates(info.treatmentLocation).longitude,
                title: info.treatmentLocation,
                location: 'Treatment Location',
                marker: InjuryMarker,
            });
        }

        return result;
    };

    const infoReducer = (accum, value) => {
        accum[value] = {
            empty:
                info[value] === null ||
                (info[value] && info[value].length === 0) ||
                info[value] === undefined,
            value: info[value],
            render: (
                <FormattedField value={info[value]} field={value} obj={info} />
            ),
        };
        return accum;
    };

    return (
        <>
            <Grid container justifyContent="center">
                {/* HEADER */}
                <Paper
                    className={classes.container}
                    elevation={0}
                    style={{ width: '100%' }}
                >
                    <Grid container justifyContent="flex-start" spacing={1}>
                        <Grid item xs={12}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Grid item xs={4}>
                                    <Grid container spacing={1}>
                                        <Grid item>
                                            <MonitorHeartIcon
                                                style={{ color: '#325599' }}
                                            />
                                        </Grid>
                                        <Grid item xs={10}>
                                            <Grid
                                                item
                                                container
                                                direction="column"
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            fontSize: '0.7em',
                                                            fontWeight: 'bold',
                                                        }}
                                                        color={'primary'}
                                                    >
                                                        Status
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                style={{
                                                    fontSize: '0.9em',
                                                }}
                                            >
                                                {info.status || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid container spacing={1}>
                                        <Grid item>
                                            <LocalHospitalIcon
                                                style={{ color: '#325599' }}
                                            />
                                        </Grid>
                                        <Grid item xs={10}>
                                            <Grid
                                                item
                                                container
                                                direction="column"
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            fontSize: '0.7em',
                                                            fontWeight: 'bold',
                                                        }}
                                                        color={'primary'}
                                                    >
                                                        Physician
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                style={{
                                                    fontSize: '0.9em',
                                                }}
                                            >
                                                {info.physician || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid container spacing={1}>
                                        <Grid item>
                                            <LocationOnIcon
                                                style={{ color: '#325599' }}
                                            />
                                        </Grid>
                                        <Grid item xs={10}>
                                            <Grid
                                                item
                                                container
                                                direction="column"
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            fontSize: '0.7em',
                                                            fontWeight: 'bold',
                                                        }}
                                                        color={'primary'}
                                                    >
                                                        Treatment Location
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                style={{
                                                    fontSize: '0.9em',
                                                    paddingLeft: 4,
                                                }}
                                            >
                                                {info.treatmentLocation || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: 10 }}>
                        <InfoGrid
                            info={infoToShow.reduce(infoReducer, {})}
                            caseInfo={undefined}
                        />
                    </Grid>
                    <Grid item xs={12} style={{ padding: 10, height: 200 }}>
                        <MultiMarkerMap
                            positions={combineLocations()}
                            classes={undefined}
                        />
                    </Grid>
                    <Grid item container spacing={1} style={{ padding: 10 }}>
                        <Grid item xs={6}>
                            <Grid
                                item
                                container
                                direction="column"
                                style={
                                    !info.treatmentLocation
                                        ? { opacity: 0.3 }
                                        : {}
                                }
                            >
                                <Grid item>
                                    <Typography
                                        variant="caption"
                                        color={'primary'}
                                        style={{
                                            fontSize: '0.7em',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Treatment Location
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography>
                                        {info.treatmentLocation || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </>
    );
};

export default BasicInfo;
